import { Badge, Button, Divider, Group, NumberInput, Select, Textarea, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useVariables } from '../../hooks/useVariables';
import { EditorItem, useEditorStore } from '../../store/editorStore';
import { fonts } from '../../utils/fonts';

interface AITextItemPanelProps {
  item: EditorItem;
  handleUpdate: (updates: Partial<EditorItem>) => void;
  handleStyleUpdate: (styleUpdates: Partial<any>) => void;
}

export function AITextItemPanel({ item, handleUpdate, handleStyleUpdate }: AITextItemPanelProps) {
  const updateItem = useEditorStore((state) => state.updateItem);
  const { variables } = useVariables();
  
  const extractPromptFromContent = (content: string): string => {
    return content;
  };
  
  const [prompt, setPrompt] = useState(extractPromptFromContent(item.content) || '');
  
  // Cập nhật prompt khi item thay đổi
  useEffect(() => {
    const extractedPrompt = extractPromptFromContent(item.content);
    if (extractedPrompt && extractedPrompt !== prompt) {
      setPrompt(extractedPrompt);
    }
  }, [item.content]);
    
  // Xử lý khi chèn biến
  const handleInsertVariable = (variableName: string) => {
    setPrompt(prev => `${prev} {{${variableName}}}`);
    
    notifications.show({
      title: 'Đã chèn biến',
      message: `Đã chèn biến {{${variableName}}} vào prompt`,
      color: 'blue',
      autoClose: 2000,
    });
  };

  // Xử lý khi prompt thay đổi
  const handlePromptChange = (value: string) => {
    setPrompt(value);
  };
  
  // Xử lý khi người dùng hoàn thành chỉnh sửa prompt
  const handlePromptBlur = () => {
    // Cập nhật nội dung của item với prompt mới
    handleUpdate({ content: `${prompt}` });
    
    // Hiển thị thông báo
    notifications.show({
      title: 'Đã lưu prompt',
      message: 'Prompt sẽ được sử dụng khi tạo thumbnail',
      color: 'green',
    });
  };
  
  return (
    <>
      <Title order={4}>Thuộc tính</Title>
      
      <Textarea
        label="Prompt"
        placeholder="Nhập prompt để tạo nội dung..."
        value={prompt}
        onChange={(e) => handlePromptChange(e.currentTarget.value)}
        onBlur={handlePromptBlur}
        minRows={6}
        maxRows={10}
        autosize
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        mb="xs"
      />
      
      {/* Phần chèn biến vào prompt */}
      {variables.length > 0 && (
        <Group mb="md">
          {variables.map((variable, index) => (
            <Button
              key={index}
              variant="outline"
              size="xs"
              onClick={() => handleInsertVariable(variable.name)}
              rightSection={
                variable.value ? (
                  <Badge 
                    color="gray" 
                    variant="light" 
                    size="xs"
                    title={`Giá trị: ${variable.value}`}
                  >
                    {variable.value.substring(0, 8)}{variable.value.length > 8 ? '...' : ''}
                  </Badge>
                ) : null
              }
            >
              {variable.name}
            </Button>
          ))}
        </Group>
      )}
      
      <Divider my="sm" label="Kiểu dáng" labelPosition="center" />
      
      <TextInput
        label="Màu chữ (mã màu)"
        value={(item.style as any).color || '#000000'}
        onChange={(e) => handleStyleUpdate({ color: e.target.value })}
        placeholder="#RRGGBB hoặc tên màu"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <TextInput
        label="Màu nền (mã màu)"
        value={(item.style as any).backgroundColor || 'transparent'}
        onChange={(e) => handleStyleUpdate({ backgroundColor: e.target.value })}
        placeholder="#RRGGBB, transparent hoặc tên màu"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <NumberInput
        label="Cỡ chữ"
        value={(item.style as any).fontSize || 16}
        onChange={(value) => handleStyleUpdate({ fontSize: value })}
        min={8}
        max={200}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <Divider my="sm" label="Viền văn bản (outline)" labelPosition="center" />
      
      <NumberInput
        label="Độ dày outline"
        value={(item.style as any).textOutlineWidth || 0}
        onChange={(value) => handleStyleUpdate({ textOutlineWidth: value })}
        min={0}
        max={10}
        step={0.5}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <TextInput
        label="Màu outline"
        value={(item.style as any).textOutlineColor || '#000000'}
        onChange={(e) => handleStyleUpdate({ textOutlineColor: e.target.value })}
        placeholder="#RRGGBB hoặc tên màu"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={(item.style as any).textOutlineWidth <= 0}
      />
      
      <Divider my="sm" label="Viền ô chứa" labelPosition="center" />
      
      <NumberInput
        label="Độ dày viền ô"
        value={(item.style as any).borderWidth || 0}
        onChange={(value) => handleStyleUpdate({ borderWidth: value })}
        min={0}
        max={20}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <TextInput
        label="Màu viền ô"
        value={(item.style as any).borderColor || '#000000'}
        onChange={(e) => handleStyleUpdate({ borderColor: e.target.value })}
        placeholder="#RRGGBB hoặc tên màu"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={(item.style as any).borderWidth <= 0}
      />
      
      <Select
        label="Font chữ"
        value={(item.style as any).fontFamily || 'Arial'}
        onChange={(value) => handleStyleUpdate({ fontFamily: value as string })}
        data={fonts}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <Select
        label="Căn chỉnh"
        value={(item.style as any).textAlign || 'left'}
        onChange={(value) => handleStyleUpdate({ textAlign: value as string })}
        data={[
          { value: 'left', label: 'Trái' },
          { value: 'center', label: 'Giữa' },
          { value: 'right', label: 'Phải' },
          { value: 'justify', label: 'Căn đều' }
        ]}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <Divider my="md" />
      
      {/* Điều khiển lớp (zIndex) */}
      <NumberInput
        label="Lớp hiển thị (zIndex)"
        value={item.zIndex}
        onChange={(value) => {
          if (typeof value === 'number') {
            updateItem(item.id, { zIndex: value });
          }
        }}
        min={0}
        max={999}
        step={1}
        hideControls={false}
        allowDecimal={false}
        clampBehavior="strict"
        description="Giá trị càng cao, phần tử càng hiển thị phía trên"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    </>
  );
} 